import {
  FieldModel,
  GeneralResourceDetail,
  MpxItemResponse,
  MpxResponseError,
  PracticeDetailFields,
  ResourceDetailsUpdate,
  TaskDataResponse,
  TraitId,
} from '@cibo/core'

import { SelectChangeEvent } from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { flatten } from 'lodash'
import { useSnackbar } from 'notistack'
import { difference, symmetricDifference, uniq } from 'ramda'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  useEqipProgramEngagementPractices,
  usePracticeRequirementModal,
} from '../../../../components'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramEngagementDetails } from '../../../../queries'
import { MultiselectCheckboxContext } from './MultiselectCheckboxContext'
import { CombinedAnswer, FieldAnswer } from './MultiselectFieldInputTypes'

interface MultiselectCheckboxProviderProps {
  children: ReactNode
  dataEntryFieldDetails?: UseQueryResult<TaskDataResponse, Error>
  proprietaryValueSource?: { data: any; retriever: (data: any, fieldId: string) => string[] }
  engagementId: string
  handleChange: (
    updates: ResourceDetailsUpdate[]
  ) => Promise<void | MpxItemResponse<GeneralResourceDetail>>
  onError?: (error: AxiosResponse<MpxResponseError, any>) => void
  onSuccess?: () => void
  onUpdating?: () => void
  optionsList?: { value: string; label: string }[]
  sources?: TraitId[] | string[]
  traitId: TraitId
  year?: number
  verifyExtraRequirements?: boolean
}

export const MultiselectCheckboxProvider = ({
  children,
  dataEntryFieldDetails,
  engagementId,
  handleChange,
  onError,
  onSuccess,
  onUpdating,
  optionsList,
  sources,
  traitId,
  year,
  proprietaryValueSource,
  verifyExtraRequirements,
}: MultiselectCheckboxProviderProps) => {
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')
  const { enqueueSnackbar } = useSnackbar()
  const { fieldModels } = useProgramEngagementContext()
  const engagementDetails = useProgramEngagementDetails({ engagementId })
  const allPractices = useEqipProgramEngagementPractices({
    engagementId,
  })
  const { setModalState } = usePracticeRequirementModal()

  const [combinedAnswer, setCombinedAnswer] = useState<Record<string, CombinedAnswer>>({})
  const [fieldAnswers, setFieldAnswers] = useState<Record<string, FieldAnswer>>({})
  const [allDisabled, setDisableAll] = useState<boolean>(false)

  const allDetailsAreCibo = useMemo(
    () =>
      Object.values(fieldAnswers).length > 0 &&
      Object.values(fieldAnswers).every(a => a.source === 'cibo'),
    [fieldAnswers]
  )

  const fullOptionsList: string[] = useMemo(
    () => allPractices.practices.map(p => p.fields.practiceId),
    [allPractices.practices]
  )

  const engagementOptionsList = useMemo(
    () =>
      !sources
        ? optionsList?.map(o => o.value) || []
        : !!optionsList
        ? (flatten(
            engagementDetails.data
              ?.map(({ result }) => sources?.map(source => (result as Record<string, any>)[source]))
              .map(item => item?.[0]?.value) ?? []
          ).filter(Boolean) as string[])
        : (flatten(
            engagementDetails.data
              ?.filter(({ traitId }) => sources?.includes(traitId))
              .map(({ result }) => result) ?? []
          ) as string[]),
    [engagementDetails.dataUpdatedAt, optionsList, sources]
  )

  const options = sources?.length ? engagementOptionsList : fullOptionsList

  // set the combined answer based on the current state of fieldAnswers and options
  useEffect(() => {
    const allOptions = uniq(['none', ...options])
    const allAnswers = Object.values(fieldAnswers)
    const allUsedAnswers = uniq(allAnswers.map(answer => answer.result).flat())
    const newCombinedAnswer: Record<string, CombinedAnswer> = {}
    allOptions.forEach(option => {
      const matchingAnswers = allAnswers?.filter(answer => answer.result?.includes(option))
      if (!matchingAnswers?.length) {
        newCombinedAnswer[option] = { value: 'unchecked', stale: false }
      } else if (matchingAnswers?.length === fieldModels.data?.items.length) {
        newCombinedAnswer[option] = { value: 'checked', stale: false }
      } else {
        newCombinedAnswer[option] = { value: 'indeterminate', stale: false }
      }
    })
    allUsedAnswers.forEach(option => {
      if (!newCombinedAnswer[option] && option !== undefined) {
        const matchingAnswers = allAnswers?.filter(answer => answer.result?.includes(option))
        newCombinedAnswer[option] = {
          value:
            matchingAnswers?.length === fieldModels.data?.items.length
              ? 'checked'
              : 'indeterminate',
          stale: true,
        }
      }
    })
    setCombinedAnswer(newCombinedAnswer)
  }, [fieldAnswers, options])

  // NOTE: this is an initial row value. It SHOULD NOT update when any details is changed by the editor.
  useEffect(() => {
    if (!dataEntryFieldDetails?.isStale && Object.keys(fieldAnswers).length === 0) {
      const initialFieldAnswers: Record<string, FieldAnswer> = {}
      fieldModels.data?.items.forEach(field => {
        if (dataEntryFieldDetails) {
          const dataEntryDetail = dataEntryFieldDetails.data?.fieldDetails
            ?.find(dataEntryField => dataEntryField.resourceId === field.id)
            ?.details.find(detail => detail.traitId === traitId && detail.year === year)
          const isImmutable = dataEntryDetail?.immutable
          initialFieldAnswers[field.resourceId] = {
            result: dataEntryDetail?.result ?? [],
            source: dataEntryDetail?.source,
            immutable: isImmutable ?? false,
          }
          if (!allDisabled && isImmutable) {
            setDisableAll(true)
          }
        } else if (proprietaryValueSource) {
          const fieldDetail = field.findDetail?.({ traitId, year })
          const proprietaryResult = proprietaryValueSource.retriever(
            proprietaryValueSource.data,
            field.resourceId
          )
          const isImmutable = fieldDetail?.immutable
          initialFieldAnswers[field.resourceId] = {
            result: proprietaryResult ?? [],
            source: fieldDetail?.source,
            immutable: isImmutable ?? false,
          }
        } else {
          const fieldDetail = field.findDetail?.({ traitId, year })
          const isImmutable = fieldDetail?.immutable
          initialFieldAnswers[field.resourceId] = {
            result: fieldDetail?.result ?? [],
            source: fieldDetail?.source,
            immutable: isImmutable ?? false,
          }
          if (!allDisabled && isImmutable) {
            setDisableAll(true)
          }
        }
      })
      setFieldAnswers(initialFieldAnswers)
    }
  }, [
    fieldModels.data,
    dataEntryFieldDetails?.isStale,
    engagementDetails.dataUpdatedAt,
    year,
    traitId,
  ])

  // If there are cibo values for the field, and the set of pre-selected current practice options
  // exactly matches the set of cibo values, then automatically save them as user values.
  useEffect(() => {
    if (allDetailsAreCibo) {
      const allAreUsed = options
        .filter(v => v !== 'none')
        .every(option => {
          return (
            combinedAnswer[option]?.value === 'checked' ||
            combinedAnswer[option]?.value === 'indeterminate'
          )
        })
      const usedAreAllSelected = Object.keys(combinedAnswer).every(option => {
        return options.includes(option)
      })
      if (allAreUsed && usedAreAllSelected) {
        saveAllCiboValues()
      }
    }
  }, [options, allDetailsAreCibo, combinedAnswer])

  const onChangeAll = (event: SelectChangeEvent<string[]>) => {
    onUpdating?.()
    const prevValue = combinedAnswer

    // 'none' is an exclusive option.
    if ((event.target.value as string[])?.includes('none') && prevValue.none.value !== 'checked') {
      setCombinedAnswer({ none: { value: 'checked', stale: false } })

      updateChangeAll({ none: { value: 'checked', stale: false } }, prevValue)
    }

    // other options need to be considered individually
    else {
      setCombinedAnswer((prevValue: Record<string, CombinedAnswer>) => {
        const newCombinedAnswer: Record<string, CombinedAnswer> = {}
        options.forEach(option => {
          if (option !== 'none') {
            if (prevValue[option]?.value === 'indeterminate') {
              newCombinedAnswer[option] = {
                value: !(event.target.value as string[]).includes(option)
                  ? // if previous value is indetermine and value is not included, maintain existing value
                    'indeterminate'
                  : 'checked',
                stale: false,
              }
            } else {
              newCombinedAnswer[option] = {
                value: !(event.target.value as string[])?.includes(option)
                  ? // if previous value is checked, unchecked or undefined and value is not included, set to unchecked
                    'unchecked'
                  : 'checked',
                stale: false,
              }
            }
          } else {
            newCombinedAnswer.none = { value: 'unchecked', stale: false }
          }
        })
        updateChangeAll(newCombinedAnswer, prevValue)

        return newCombinedAnswer
      })
    }
  }

  const updateChangeAll = useCallback(
    (
      passedCombinedAnswer: Record<string, CombinedAnswer>,
      prevValue: Record<string, CombinedAnswer>
    ) => {
      let fieldUpdates: undefined | ResourceDetailsUpdate[] = []
      // find the id that's different between prevValue and passedCombinedAnswer
      const changedId = Object.keys(passedCombinedAnswer).find(
        key => passedCombinedAnswer[key]?.value !== prevValue[key]?.value
      )

      if (passedCombinedAnswer.none?.value === 'checked') {
        fieldUpdates = fieldModels.data?.items.map(
          field =>
            ({
              resourceId: field.resourceId,
              details: [
                {
                  traitId,
                  year,
                  result: ['none'],
                },
              ],
            } as ResourceDetailsUpdate)
        )
      } else {
        fieldUpdates = fieldModels.data?.items.map(field => {
          const existingFieldValue = dataEntryFieldDetails
            ? dataEntryFieldDetails.data?.fieldDetails
                ?.find(({ resourceId }) => resourceId === field.id)
                ?.details.find(detail => detail.traitId === traitId)?.result
            : (field.findDetail?.({ traitId, year })?.result as string[])
          const newFieldValue = options.map(option => {
            // if all values is indeterminate, maintain field value
            if (passedCombinedAnswer[option]?.value === 'indeterminate') {
              return existingFieldValue?.find((value: string) => option === value)
                ? option
                : undefined
            }
            // if all values is checked, add the value
            if (passedCombinedAnswer[option]?.value === 'checked') {
              return option
            }
            // if all values is unchecked or undefined, remove the value
            return undefined
          })
          return {
            resourceId: field.resourceId,
            details: [
              {
                traitId,
                year,
                result: !!newFieldValue.filter(Boolean).filter(a => a !== 'none').length
                  ? newFieldValue.filter(Boolean).filter(a => a !== 'none')
                  : undefined,
              },
            ],
          } as ResourceDetailsUpdate
        })
      }

      if (!!fieldUpdates) {
        const previousFieldAnswers = fieldAnswers
        const updatedFieldAnswers: Record<string, FieldAnswer> = {}
        fieldUpdates.forEach(fieldUpdate => {
          updatedFieldAnswers[fieldUpdate.resourceId] = {
            result: fieldUpdate.details[0].result || [],
            source: 'user',
            immutable: false,
          }
        })
        const practice = allPractices.practiceContentQuery.data?.items?.find(
          // @ts-ignore contentful's typing doesn't believe practiceId is a string but it is!
          opt => opt.fields.practiceId === changedId
        )

        const onClickSelect = () => {
          setFieldAnswers(updatedFieldAnswers)
          handleChange(fieldUpdates)
            .then(() => {
              onSuccess?.()
              setDisableAll(false)
            })
            .catch(error => {
              onError?.(error)
              enqueueSnackbar(t('failedToSave'), {
                variant: 'error',
              })
              setCombinedAnswer(prevValue)
              setDisableAll(false)
              setFieldAnswers(previousFieldAnswers)
            })
        }
        if (
          verifyExtraRequirements &&
          changedId &&
          practice?.fields.requirementText &&
          passedCombinedAnswer[changedId]?.value === 'checked'
        ) {
          setModalState({
            open: true,
            // @ts-ignore contentful's typing doesn't believe practiceId is a string but it is!
            practice: { fields: practice.fields as PracticeDetailFields },
            onClickSelect,
          })
        } else {
          onClickSelect()
        }
      }
    },
    [
      fieldModels.data,
      dataEntryFieldDetails,
      fieldAnswers,
      options,
      traitId,
      year,
      handleChange,
      onSuccess,
      onError,
      t,
      enqueueSnackbar,
    ]
  )

  const saveCiboValues = useCallback(
    (fieldId: string) => {
      const setValue = (value: FieldAnswer) => {
        return setFieldAnswers(prev => ({ ...prev, [fieldId]: value }))
      }
      onUpdating?.()
      const prevValue = fieldAnswers[fieldId]
      setValue({ result: prevValue.result, source: 'user', immutable: false })
      handleChange([
        {
          resourceId: fieldId,
          details: [
            {
              traitId,
              year,
              result: prevValue.result,
            },
          ],
        },
      ])
        .then(onSuccess)
        .catch(error => {
          onError?.(error)
          setValue(prevValue)
        })
    },
    [onSuccess, handleChange, traitId, year, fieldAnswers, onUpdating, onError]
  )

  const saveAllCiboValues = useCallback(() => {
    const prevValue = fieldAnswers
    const updatedFieldAnswers: Record<string, FieldAnswer> = {}
    fieldModels.data?.items.forEach(field => {
      updatedFieldAnswers[field.resourceId] = {
        result: fieldAnswers[field.resourceId]?.result,
        source: 'user',
        immutable: false,
      }
    })
    const fieldUpdates = fieldModels.data?.items.map(
      field =>
        ({
          resourceId: field.resourceId,
          details: [
            {
              traitId,
              year,
              result: fieldAnswers[field.resourceId]?.result,
            },
          ],
        } as ResourceDetailsUpdate)
    )
    setFieldAnswers(updatedFieldAnswers)
    fieldUpdates &&
      handleChange(fieldUpdates)
        .then(() => {
          onSuccess?.()
          setDisableAll(false)
        })
        .catch(error => {
          onError?.(error)
          enqueueSnackbar(t('failedToSave'), {
            variant: 'error',
          })
          setDisableAll(false)
          setFieldAnswers(prevValue)
        })
  }, [
    fieldAnswers,
    fieldModels.data,
    handleChange,
    onSuccess,
    onError,
    enqueueSnackbar,
    t,
    traitId,
    year,
  ])

  const onChangeField = (
    event: SelectChangeEvent<string[]>,
    row: FieldModel,
    value: FieldAnswer
  ) => {
    onUpdating?.()
    const prevValue = value
    const newValue =
      ((event.target.value as string[]) || []).includes('none') &&
      !prevValue?.result?.includes('none')
        ? // 'none' is an exclusive option.
          { result: ['none'], source: 'user', immutable: prevValue?.immutable ?? false }
        : {
            result: (event.target.value as string[])?.filter(option => option !== 'none'),
            source: 'user',
            immutable: prevValue?.immutable ?? false,
          }
    const changedId = symmetricDifference(newValue.result, prevValue.result)[0]
    const idAdded = !!difference(newValue.result, prevValue.result).length

    const practice = allPractices.practiceContentQuery.data?.items?.find(
      // @ts-ignore contentful's typing doesn't believe practiceId is a string but it is!
      opt => opt.fields.practiceId === changedId
    )

    const onClickSelect = () => {
      setFieldAnswers(prev => ({ ...prev, [row.resourceId]: newValue }))
      handleChange([
        {
          resourceId: row.resourceId,
          details: [
            {
              traitId,
              year,
              result: newValue.result?.length ? newValue.result : undefined,
            },
          ],
        },
      ])
        .then(onSuccess)
        .catch(error => {
          onError?.(error)
          setFieldAnswers(prev => ({ ...prev, [row.resourceId]: prevValue }))
        })
    }

    if (verifyExtraRequirements && changedId && practice?.fields.requirementText && idAdded) {
      setModalState({
        open: true,
        // @ts-ignore contentful's typing doesn't believe practiceId is a string but it is!
        practice: { fields: practice.fields as PracticeDetailFields },
        onClickSelect,
      })
    } else {
      onClickSelect()
    }
  }

  return (
    <MultiselectCheckboxContext.Provider
      value={{
        allDetailsAreCibo,
        allDisabled,
        combinedAnswer,
        fieldAnswers,
        onChangeAll,
        onChangeField,
        optionDisplayString: (o: string) =>
          allPractices.practices.find(opt => opt.fields.practiceId === o)?.fields.name ||
          t(`choice-${o}`, { ns: '@cibo/programs/EqipPracticeChoices' }),
        options,
        programEngagementFields: fieldModels,
        saveAllCiboValues,
        saveCiboValues,
        setFieldAnswers,
        traitId,
        updateChangeAll,
        year,
      }}
    >
      {children}
    </MultiselectCheckboxContext.Provider>
  )
}
